import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/utils/media";
import { TYPE_LABEL } from "@/utils/media";

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const go = (next: number) => {
    setIndex((next + items.length) % items.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(delta < 0 ? index + 1 : index - 1);
    }
  };

  return (
    <section
      className="relative w-full h-[520px] md:h-[640px] overflow-hidden bg-foreground-900"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="焦点推荐"
    >
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
        >
          <img
            src={item.poster}
            alt={`${item.title} 焦点推荐海报`}
            title={`${item.title} 爱情影视`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-8 lg:px-16">
              <div className="max-w-2xl animate-fade-up">
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-2.5 py-1 text-xs rounded-md bg-primary-500 text-background-50 font-medium">
                    {TYPE_LABEL[item.type]}
                  </span>
                  <span className="px-2.5 py-1 text-xs rounded-md bg-background-50/15 text-background-50 border border-background-50/25">
                    焦点推荐
                  </span>
                  <span className="px-2.5 py-1 text-xs rounded-md bg-background-50/15 text-background-50 border border-background-50/25">
                    {item.year}
                  </span>
                </div>
                <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight">
                  {item.title}
                </h1>
                <p className="mt-2 text-sm md:text-base text-background-200 flex items-center gap-3">
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-400"></i>
                    {item.rating.toFixed(1)}
                  </span>
                  <span>{item.region}</span>
                  <span>导演 {item.director}</span>
                </p>
                <p className="mt-4 text-sm md:text-base text-background-100/90 leading-relaxed max-w-xl line-clamp-3">
                  {item.synopsis}
                </p>
                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/detail/${item.id}`}
                    className="inline-flex items-center gap-2 px-6 h-11 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-circle-line text-lg"></i>
                    查看详情
                  </Link>
                  <Link
                    to={`/category/${item.type === "movie" ? "movies" : "tv"}`}
                    className="inline-flex items-center gap-2 px-6 h-11 rounded-md bg-background-50/15 hover:bg-background-50/25 text-background-50 border border-background-50/30 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    更多{TYPE_LABEL[item.type]}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => go(index - 1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/35 text-background-50 text-xl backdrop-blur transition-colors cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line"></i>
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/35 text-background-50 text-xl backdrop-blur transition-colors cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => go(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-7 bg-primary-400" : "w-1.5 bg-background-50/50"
            }`}
            aria-label={`切换到第 ${i + 1} 张`}
          />
        ))}
      </div>
    </section>
  );
}